import { useEffect, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { NavLink, useLocation, useNavigate } from 'react-router'
import { QuickAdd } from '@/components/QuickAdd'
import { Wordmark } from '@/components/Wordmark'
import { CalendarIcon, MoreIcon, PlanIcon, SearchIcon } from '@/components/icons'
import { useWide } from '@/hooks/useWide'
import { usePrefs } from '@/store/prefs'
import { setFeedbackOpen, setSearchOpen } from '@/store/ui'
import './AppShell.css'

const TABS = [
  { to: '/plan', label: 'Plan', Icon: PlanIcon, view: 'plan' as const },
  { to: '/calendar', label: 'Calendar', Icon: CalendarIcon, view: 'calendar' as const },
  { to: '/settings', label: 'More', Icon: MoreIcon, view: null },
]

/** More: a small menu rather than a page, with Settings and Send feedback. */
function MoreTab({ active, wide }: { active: boolean; wide: boolean }) {
  const navigate = useNavigate()
  const { pathname } = useLocation()
  const [open, setOpen] = useState(false)
  // where the menu hangs: above the tab on a phone, below it in a wide header
  const [anchor, setAnchor] = useState<DOMRect | null>(null)
  // a change of page closes it
  const [seen, setSeen] = useState(pathname)
  if (seen !== pathname) {
    setSeen(pathname)
    setOpen(false)
  }
  useEffect(() => {
    if (!open) return
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setOpen(false)
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [open])
  return (
    <span className="shell__more">
      <button type="button" className={`shell__tab ${active || open ? 'shell__tab--on' : ''}`} aria-haspopup="menu" aria-expanded={open} aria-current={active ? 'page' : undefined} onClick={(e) => {
          setAnchor(e.currentTarget.getBoundingClientRect())
          setOpen((v) => !v)
        }}
      >
        <MoreIcon size={22} />
        <span>More</span>
      </button>
      {open &&
        anchor &&
        createPortal(
        <>
          <button type="button" className="shell__menu-scrim" aria-label="Close menu" tabIndex={-1} onClick={() => setOpen(false)} />
          <div
            className="shell__menu"
            role="menu"
            aria-label="More"
            style={wide ? { top: anchor.bottom + 6, left: anchor.left } : { right: Math.max(8, window.innerWidth - anchor.right), bottom: window.innerHeight - anchor.top + 6 }}
          >
            <button
              type="button"
              role="menuitem"
              className="shell__menu-item"
              onClick={() => {
                setOpen(false)
                navigate('/settings')
              }}
            >
              Settings
            </button>
            <button
              type="button"
              role="menuitem"
              className="shell__menu-item"
              onClick={() => {
                setOpen(false)
                setFeedbackOpen(true)
              }}
            >
              Send feedback
            </button>
          </div>
        </>,
          document.body,
        )}
    </span>
  )
}

interface AppShellProps {
  children: ReactNode
}

/**
 * The chrome every view shares: on a phone the bottom tab bar within thumb
 * reach; from 900px a header with the logo, the tabs, quick add and search.
 * The highlighted tab is the only view title. Each view renders its own top row.
 */
export function AppShell({ children }: AppShellProps) {
  const { pathname } = useLocation()
  const wide = useWide()
  const lastView = usePrefs((s) => s.view)
  const onTask = pathname.startsWith('/task/')
  const current = onTask ? TABS.find((t) => t.view === lastView) : TABS.find((t) => pathname.startsWith(t.to))
  const tabs = (
    <nav className="shell__tabs" aria-label="Views">
      {TABS.map(({ to, label, Icon }) => {
        const active = current?.to === to
        if (to === '/settings') return <MoreTab key={to} active={active} wide={wide} />
        return (
          <NavLink key={to} to={to} className={`shell__tab ${active ? 'shell__tab--on' : ''}`} aria-current={active ? 'page' : undefined}>
            <Icon size={22} />
            <span>{label}</span>
          </NavLink>
        )
      })}
    </nav>
  )
  return (
    <div className="shell">
      <h1 className="sr-only">{current?.label ?? 'Cuplzap'}</h1>
      {wide && (
        <header className="shell__head">
          <Wordmark className="shell__logo" />
          {tabs}
          <QuickAdd placement="header" />
          <button type="button" className="shell__search" onClick={() => setSearchOpen(true)}>
            <SearchIcon size={16} />
            <span>Search</span>
          </button>
        </header>
      )}
      <div className="shell__content">{children}</div>
      {!wide && tabs}
    </div>
  )
}
